import React, { forwardRef, useEffect, useState, useRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { Form, Col, Row } from 'react-bootstrap';
import { useForm, FormProvider } from 'react-hook-form';
import { sub } from 'date-fns';
import {
  CircularProgress,
  SvgIcon,
  Button,
  Tooltip,
  createTheme,
  ThemeProvider,
  FormGroup,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { NumericFormat } from 'react-number-format';
import { customValidators, object, string, date } from '../../../Helpers/Validators/customValidators';
import { QuoteProductID } from '../../Common/QuoteProductID';
import QuoterFilter, { getSavedFiltersForCurrentFlow } from '../Helpers/QuoterFilter';
import States from '../../../Helpers/Lists/States';
import { yupResolver } from "@hookform/resolvers/yup";
import StatesWithDisabled from '../../../Helpers/Lists/StatesWithDisabled';
import TobaccoUseExtended from '../../../Helpers/Lists/TobaccoUseExtended';
import { InputMaskHeight, InputMaskWeight, InputMaskDate } from '../../../Helpers/Formatters';
import { TextInput, SelectInput, StyledRadioInput } from '../../../Helpers/Inputs';
import UserIcon from '../../../../icons/user.svg?react';
import { calculateExactAge, calculateNearestAge } from '../../Common/Helpers/AgeCalculation';
import {
  getBasicInfo,
  setBasicInfo,
  setSessionStorageItem,
  getSessionStorageItem,
  SESSION_STORAGE_ACCOUNT_KEY,
  SESSION_STORAGE_SAVED_QUOTE_DATA,
  SESSION_STORAGE_CUSTOMIZED_TEASER_KEY,
  SESSION_STORAGE_TERM_SORT_FILTER_KEY
} from '../../../../sessionStorage';
import ShieldCheckmarkIcon from '../../../../icons/shield-checkmark.svg?react';
import { GetMCTQuotes } from '../Common/GetMCTQuotes';
import Age from '../../../Helpers/Dates/Age';
import ModalWarning from '../../../Helpers/Widgets/ModalWarning';
import { getFaceAmountFromAge } from '../../../Helpers/Lists/CoverageAmounts/AmericoCoverageAmounts';
import { getTermLengthFromAge } from '../../../Helpers/Lists/CoverageLengths/AmericoCoverageLengths';
import TestimonialPanel from '../../../Common/Testimonials/TestimonialPanel';
import './AboutMe.scss';

const TermAboutMeForm = forwardRef((props, ref) => {
  const innerRef = useRef(null);
  const { isPartnerReferralEmailCaptureActive, enablePartnerReferralEmailCapture } = props.emailCapture || {};
  const termSettings = getSessionStorageItem(SESSION_STORAGE_TERM_SORT_FILTER_KEY);
  const theme = createTheme({
    palette: {
      primary: {
        main: `${props.brandTheme ? props.brandTheme : "#3f51b5"}`,
      },
    },
    typography: {
      fontFamily: "museo-sans",
      body1: {
        fontSize: 14,
        fontWeight: 500,
      },
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: 16,
          },
          root: {
            height: 56,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
          },
          underline: {
            "&::before": {
              borderBottom: "solid 2px #b2b2b2",
            },
            "&:hover::before": {
              borderBottomWidth: "2px",
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: 16,
            color: "rgba(0,0,0,0.6)",
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginTop: 0,
            paddingTop: 0,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          shrink: {
            color: "rgba(0,0,0,0.3) !important",
          },
          root: {
            '&.Mui-error': {
              fontWeight: 400,
              fontSize: '17px',
            },
          },
        },
      },
      MuiNativeSelect: {
        styleOverrides: {
          fontSize: 16,
          icon: {
            color: "rgba(0,0,0,1)",
          },
          iconFilled: {
            right: 16,
          },
        },
      },
    },
  });

  const navigate = useNavigate();
  const basicInfo = getBasicInfo();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState(TermAboutMeForm.DefaultFormState(false, basicInfo));
  const [teaserRate, setTeaserRate] = useState(null);
  const [purlMismatch, setPurlMismatch] = useState(false);
  const [initialBasicInfo] = useState(basicInfo);
  const age = Age(formState.birthdate);
  const coverageAmountAsPerAge = (basicInfo && basicInfo.faceAmount) || getFaceAmountFromAge(age);
  const termLengthAsPerAge = (basicInfo && basicInfo.termLength) || getTermLengthFromAge(age);

  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const savedQuote = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
  const termNavigationState = props.isSimplifiedIssueTerm ? { state: { isFromSimplifiedIssueTerm: true } } : undefined;
  const accountProductTypeSettings = props ? accountObj.accountProducts.find(product => props.isSimplifiedIssueTerm
    ? product.productLandingUrl === '/landing/simplified-issue-term'
    : product.productLandingUrl === '/landing/term-insurance' || product.productLandingUrl === '/landing/loan-protection') : null;

  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [filterObject, setFilterObject] = useState(getSavedFiltersForCurrentFlow(termSettings, props.isSimplifiedIssueTerm));
  const [termLength, setTermLength] = useState(20);
  const [coverageAmount, setCoverageAmount] = useState(500000);
  const [healthClass, setHealthClass] = useState('PP');
  const [filteredQuoteResults, setFilteredQuoteResults] = useState([]);
  const [fastestRate, setFastestRate] = useState('');
  const [lowestPrice, setLowestPrice] = useState('');

  const [quoteResults, setQuoteResults] = useState([]);
  const [quoteLowestPrice, setQuoteLowestPrice] = useState(null);
  const [quoteFastestProcess, setQuoteFastestProcess] = useState(null);
  const [quoteMostPopular, setQuoteMostPopular] = useState(null);
  const [saveQuoteData, setSaveQuoteData] = useState(null);
  const [saveQuoteContact, setSaveQuoteContact] = useState(savedQuote);
  const [isQuoteSaved, setQuoteSaved] = useState(savedQuote.quoteSaved);

  const [tooltip, setTooltip] = useState(false);
  const handleTooltipAboutClose = () => {
    setTooltip(false);
  };
  const handleTooltipAboutOpen = () => {
    setTooltip(true);
  };

  const formMethods = useForm({
    resolver: yupResolver(TermAboutMeForm.ValidationSchema(props.ageSpecs)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: formState
  });
  const { handleSubmit, trigger, formState: { errors } } = formMethods;

  useEffect(() => {
    if (saveQuoteContact?.emailAddress !== "") {
      setSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA, {
        "quoteSaved": isQuoteSaved,
        "firstName": saveQuoteContact?.firstName,
        "lastName": saveQuoteContact?.lastName,
        "emailAddress": saveQuoteContact?.emailAddress,
      });
    }
    setCurrentSavedQuoteData();
  }, [isQuoteSaved]);

  const sorter = (a, b, sortByField) => {
    switch (sortByField) {
      case 'lowestPrice':
        return a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore;
      case 'highestPrice':
        return b.monthlyRate - a.monthlyRate || a.totalUnweightedScore - b.totalUnweightedScore;
      case 'speed':
        return b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore;
      case 'bestSellers':
        return
      case 'highestRated':
        return b.companyRatingsScore - a.companyRatingsScore || a.totalUnweightedScore - b.totalUnweightedScore;
      case 'fastest':
        return b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore;
      default:
        break;
    }
  };

  const filterResults = (quotes) => {
    return QuoterFilter.filterResults(filterObject, quotes);
  };

  useEffect(() => {
    if (quoteResults.length > 0) {
      quoteResults.sort((a, b) => b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
      quoteResults[0].isFastest = true;
      setFastestRate(quoteResults[0].monthlyRate);
      quoteResults.sort((a, b) => a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
      setLowestPrice(quoteResults[0].monthlyRate);
      quoteResults[0].isBestPrice = true;

      quoteResults.sort((a, b) => sorter(a, b, (!!props.quoteResultSortDefault ? props.quoteResultSortDefault : 'lowestPrice')));
      const filteredQuotes = filterResults(quoteResults);
      setFilteredQuoteResults(filteredQuotes);

    } else {
      setFilteredQuoteResults([]);
    }

    if (quoteResults.length === 0) {
      return;
    }
    else {
      const minRate = quoteResults[0].monthlyRate;
      setTeaserRate(minRate);
    }

  }, [quoteResults]);

  const getQuoteFastestProcess = (results, lowest) => {
    const sortedResults = results.sort((a, b) => a.speedScore - b.speedScore);
    const product = results.find(product => !!product.isFastest);

    if (product && lowest && product.productID === lowest.productID) {
      return sortedResults[sortedResults.length - 2];
    }
    return product;
  };

  const getQuoteMostPopular = (results, fastest, lowest) => {
    const productIds = [QuoteProductID.LGA_PRODUCT_ID, QuoteProductID.BANNER_OPTERM_PRODUCT_ID, QuoteProductID.PENN_OPTERM_PRODUCT_ID, QuoteProductID.SYMETRA_SWIFT_TERM_PRODUCT_ID, QuoteProductID.PACIFIC_LIFE_PROMISE_TERM_PRODUCT_ID, QuoteProductID.AMERICO_TERM_PRODUCT_ID];

    if (!props.isAmerico && fastest && lowest) {
      let product = null;

      for (const id of productIds) {
        product = results.find(product => product.productID === id && fastest.productID !== id && lowest.productID !== id);
        if (product) {
          break;
        }
      }
      return product;
    }
    if (props.isAmerico && fastest && lowest) {
      let product = null;

      product = results.find(product => product.productID === QuoteProductID.AMERICO_TERM_PRODUCT_ID && fastest.productID !== QuoteProductID.AMERICO_TERM_PRODUCT_ID && lowest.productID !== QuoteProductID.AMERICO_TERM_PRODUCT_ID);

      if (product) {
        return product;
      } else {

        for (const id of productIds) {
          product = results.find(product => product.productID === id && fastest.productID !== id && lowest.productID !== id);
          if (product) {
            break;
          }
        }
        return product;
      }
    }
  };

  const setQuoteValues = () => {
    const quotedLowestPrice = quoteResults.find(product => !!product.isBestPrice);
    const quotedFastestProcess = getQuoteFastestProcess(quoteResults, quotedLowestPrice);
    const quotedMostPopular = getQuoteMostPopular(quoteResults, quotedFastestProcess, quotedLowestPrice);

    const lowestPriceOdj = {
      carrierLogo: quotedLowestPrice && quotedLowestPrice.logoUrl,
      rating: quotedLowestPrice && quotedLowestPrice.rating,
      monthlyRate: quotedLowestPrice && quotedLowestPrice.monthlyRate && quotedLowestPrice.monthlyRate.toFixed(2),
    };

    const fastestProcessOdj = {
      carrierLogo: quotedFastestProcess && quotedFastestProcess.logoUrl,
      rating: quotedFastestProcess && quotedFastestProcess.rating,
      monthlyRate: quotedFastestProcess && quotedFastestProcess.monthlyRate && quotedFastestProcess.monthlyRate.toFixed(2),
    };

    const mostPopularOdj = {
      carrierLogo: quotedMostPopular && quotedMostPopular.logoUrl,
      rating: quotedMostPopular && quotedMostPopular.rating,
      monthlyRate: quotedMostPopular && quotedMostPopular.monthlyRate && quotedMostPopular.monthlyRate.toFixed(2),
    };

    setQuoteLowestPrice(lowestPriceOdj);
    setQuoteFastestProcess(fastestProcessOdj);
    setQuoteMostPopular(mostPopularOdj);
  };

  const setCurrentSavedQuoteData = () => {
    const newData = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
    setSaveQuoteContact(newData);
  };

  const trimString = (str) => {
    if (str !== null) {
      return str?.replace(/\s+/g, " ");
    }
    return "";
  };

  useEffect(() => {
    setSaveQuoteContact(props.saveQuoteContact);
  }, [props.saveQuoteContact]);

  useEffect(() => {
    if (quoteResults.length > 0) {
      setQuoteValues();
    }
  }, [loading]);

  useEffect(() => {
    props.setEmailCaptureActive?.(submitDisabled);
  }, [submitDisabled]);

  const covrDisclaimer = 'Insurance products are sold through Covr Financial Technologies Inc ("Covr"). Covr sells insurance as an agent or broker by contractual agreements with its insurance carrier providers. Covr is licensed in the following states: Delaware (3000067942), Idaho (607086), California (0L77229).';

  useEffect(() => {
    const quoteData = {
      accountId: accountObj.account.accountId,
      accountName: accountObj.account.accountName,
      oneClickAccountId: accountObj.account.oneClickAccountId,
      accountProductTypeSettingId: accountProductTypeSettings?.accountProductTypeSettingId,
      productType: props.isAmerico ? "LoanProtector" : "TermA",
      sessionId: accountObj.sessionId,
      accountColorLogoUrl: accountObj.account.logoUrl,
      firstName: saveQuoteContact?.firstName,
      lastName: saveQuoteContact?.lastName,
      emailAddress: saveQuoteContact?.emailAddress,
      gender: basicInfo.gender,
      dateOfBirth: basicInfo.birthdate,
      state: basicInfo.state,
      height: trimString(basicInfo.height),
      weight: trimString(basicInfo.weight),
      tobaccoStatus: props.isAmerico ? (basicInfo.tobacco !== 'Never' && basicInfo.tobacco !== 'false') : basicInfo.tobacco,
      coverageAmount: coverageAmount,
      termLength: termLength,
      healthClass: healthClass,
      quoteLowestPrice: quoteLowestPrice,
      quoteFastestProcess: quoteFastestProcess,
      quoteMostPopular: quoteMostPopular,
      quotedLowestPrice: !!quoteLowestPrice ? quoteLowestPrice.monthlyRate : null,
      signatureEmail: "insuranceapplication@covrtech.com",
      signaturePhone: accountObj.account.phone,
      covrDisclaimer: covrDisclaimer,
      disclaimerHtml: accountObj.account.disclaimerHtml ? accountObj.account.disclaimerHtml : null,
    };
    setSaveQuoteData(quoteData);
  }, [quoteLowestPrice, quoteFastestProcess, quoteMostPopular, termLength, coverageAmount, healthClass, saveQuoteContact]);

  useEffect(() => {
    props.setSaveQuoteData?.(saveQuoteData);
  }, [saveQuoteData]);

  const runQuote = (getIsSubscribed) => {
    const coverageAmount = coverageAmountAsPerAge;
    const termLength = termLengthAsPerAge;
    let healthClass = 'PP';

    if (props.teaserQuoteParams && props.teaserQuoteParams.healthClass) {
      healthClass = props.teaserQuoteParams.healthClass;
    }

    GetMCTQuotes(formState, healthClass, coverageAmount.toString(10), termLength.toString(10), setLoading, getIsSubscribed, null, setQuoteResults, ['Level'], null, null, false, true);
  };

  const isValidHeight = (height) => {
    if (height) {
      const feet = height.split('ft')[0].trim();
      const inches = height.split('ft')[1].split('in')[0].trim();
      if (feet > 0 && inches >= 0 && inches !== "") {
        return true;
      }
      return false;
    }
    return false;
  };

  const isValidWeight = (weight) => {
    const wts = parseInt(weight);
    if (weight && wts >= 78 && wts <= 395) {
      const wt = weight.split(' ')[0].trim();
      if (wt) {
        return true;
      }
      return false;
    }
    return false;
  };

  const isValidDate = (birthdate) => {
    const date = new Date(birthdate);
    const age = Age(birthdate);
    return (date !== "Invalid Date" && age <= props.ageSpecs.max && age >= props.ageSpecs.min);
  };

  const inValidBasicInfo = () => {
    if (formState.gender && formState.state && formState.tobacco
      && isValidHeight(formState.height) && isValidWeight(formState.weight) && isValidDate(formState.birthdate)) {
      return true;
    }
    return false;
  };

  let debounce = null;
  const GetQuotes = () => {
    if ((props.term30EnableTeaserRate || !!isPartnerReferralEmailCaptureActive) && inValidBasicInfo()) {
      setLoading(true);
      let isSubscribed = true;
      if (debounce) {
        clearTimeout(debounce);
      }
      debounce = setTimeout(() => runQuote(() => isSubscribed), 1000);
      return () => {
        clearTimeout(debounce);
        isSubscribed = false;
      };
    }
  };

  useEffect(() => {
    setSubmitDisabled(!!isPartnerReferralEmailCaptureActive && !!enablePartnerReferralEmailCapture);
    props.setEmailCaptureActive?.(submitDisabled);

    if (!props.hideCoverage || !!isPartnerReferralEmailCaptureActive) {
      GetQuotes();
    }
  }, []);

  useEffect(() => {
    GetQuotes();
  }, [formState.gender, formState.birthdate, formState.state, formState.height, formState.weight, formState.tobacco]);

  useEffect(() => {
    (async () => {
      if (props.isPurlVerification && props.selectedQuote && !initialBasicInfo.birthdate) {
        let mismatch = false;
        if (formState.birthdate !== '' && (await trigger('birthdate'))) {
          let initialAge, newAge;
          if (props.selectedQuote.ageDetermination && props.selectedQuote.ageDetermination.toLowerCase() === 'nearest') {
            initialAge = calculateNearestAge(new Date(initialBasicInfo.fakeBirthdate));
            newAge = calculateNearestAge(new Date(formState.birthdate));
          } else {
            initialAge = calculateExactAge(new Date(initialBasicInfo.fakeBirthdate));
            newAge = calculateExactAge(new Date(formState.birthdate));
          }
          mismatch = mismatch || initialAge !== newAge;
        }
        mismatch = mismatch || initialBasicInfo.gender !== formState.gender;
        mismatch = mismatch || initialBasicInfo.state !== formState.state;
        mismatch = mismatch || initialBasicInfo.tobacco !== formState.tobacco;
        setPurlMismatch(mismatch);
      }
    })();
  }, [formState]);

  const handleValueChange = (field, value) => {
    setFormState({ ...formState, [field]: value });
    if (field === 'gender' || field === 'tobacco' || field === 'birthdate') {
      trigger(field);
    }
  };


  function calculateTotalHeight() {
    const radix = 10;
    const heightArray = formState.height.split(" ");
    const heightFeet = parseInt(heightArray[0], radix);
    let heightInches;
    for (let i = 1; i < heightArray.length; i++) {
      if (heightArray[i] !== "" && heightArray[i] !== "ft") {
        heightInches = parseInt(heightArray[i], radix);
        break;
      }
    }
    ;
    return heightFeet * 12 + heightInches;
  }

  useImperativeHandle(ref, () => ({
    onSubmit,
    dispatchSubmit: () => {
      innerRef.current?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    get element() {
      return innerRef.current;
    }
  }));

  const onSubmit = () => {

    const totalHeight = calculateTotalHeight();
    const weight = formState.weight.split(" ")[0];

    if (props.validateBMI != null && props.validateBMI(totalHeight, formState.bmi, weight) && props.onDeclined != null) {
      props.onDeclined();
    } else {
      // Save formState to basicinfo
      const basicInfoFromSessionStorage = getBasicInfo();
      basicInfoFromSessionStorage.gender = formState.gender;
      basicInfoFromSessionStorage.birthdate = formState.birthdate;
      basicInfoFromSessionStorage.state = formState.state;
      basicInfoFromSessionStorage.height = formState.height;
      basicInfoFromSessionStorage.weight = formState.weight;
      basicInfoFromSessionStorage.email = formState.email;
      basicInfoFromSessionStorage.tobacco = formState.tobacco;
      basicInfoFromSessionStorage.usResident = formState.usResident;

      setBasicInfo(basicInfoFromSessionStorage);

      if (submitDisabled && isPartnerReferralEmailCaptureActive) {
        props.setModalSaveQuoteIsOpen(submitDisabled);
      }

      // Set teaser rate
      const teaserRateStorage = {
        params: {
          termLength: termLengthAsPerAge,
          faceAmount: coverageAmountAsPerAge,
          healthClass: props.teaserQuoteParams && props.teaserQuoteParams.healthClass,
        },
        rate: teaserRate,
      };
      setSessionStorageItem(SESSION_STORAGE_CUSTOMIZED_TEASER_KEY, teaserRateStorage);

      if (props.openEditPopUp) {
        if (props.isAmerico) {
          navigate('/loan-protection/quote-results');
        } else {
          navigate('/term-insurance/quote-results', termNavigationState);
        }
        props.setOpenEditPopUp(false, purlMismatch);
      } else {
        props.onNext();
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className={`basic-info-about-me-term`}>
        <FormProvider {...formMethods}>
          <Form onSubmit={handleSubmit(onSubmit)} ref={innerRef}>
            <Row className='justify-content-center'>
              <Col lg={12}>
                {props.showHeader && (
                  <React.Fragment>
                    <Row className="justify-content-center">
                      <SvgIcon color='primary' component={UserIcon} className="icons" fontSize="large" />
                    </Row>
                    <Row className="justify-content-center">
                      <h3 className="py-3">Profile</h3>
                      <Tooltip
                        title="This information should represent the person who will be insured and covered by the policy, whether that's you or someone else."
                        id="about-tooltip" aria-describedby="about-tooltip" aria-label="more about sort by price"
                        className="toolInfo" role="tooltip"
                        enterTouchDelay={0} leaveTouchDelay={5000}
                        onClose={() => handleTooltipAboutClose()}
                        open={tooltip}
                        slotProps={{
                          tooltip: {
                            className: 'light-tooltip',
                            sx: { fontSize: '14px' }
                          },
                        }}
                      >
                        <button type='button' id="about" role="tooltip" className="quote-tooltip"
                          onMouseEnter={() => handleTooltipAboutOpen()} onClick={() => handleTooltipAboutOpen()}
                          tabIndex="0" aria-disabled="true" aria-describedby="about"><InfoIcon
                            color="action" /></button>
                      </Tooltip>
                    </Row>
                  </React.Fragment>
                )}

                <Row>
                  <Col>
                    <FormGroup>
                      <StyledRadioInput
                        name="gender"
                        id="gender"
                        label=""
                        value={formState.gender}
                        options={[{ value: "F", label: "Female", icon: 'female' }, {
                          value: "M",
                          label: "Male",
                          icon: 'male',
                        }]}
                        onChange={e => handleValueChange('gender', e)}
                        isBlankIcon={true}
                        isBlankCheckMark={false}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row className='mt-2'>
                  <Col>
                    <div className='form-group mb-3'>
                      <TextInput
                        name="birthdate"
                        id="birthdate"
                        label="Date of Birth"
                        value={formState.birthdate}
                        onChange={(e) => handleValueChange('birthdate', e.target.value)}
                        InputProps={{
                          inputComponent: InputMaskDate,
                        }}
                      />
                    </div>
                  </Col>
                </Row>

                <Row className='mt-2'>
                  <Col>
                    <div className='form-group mb-3'>
                      <SelectInput
                        name="state"
                        id="state"
                        value={formState.state}
                        onChange={(e) => handleValueChange('state', e.target.value)}
                        label="State"
                        selectOptions={props.unavailableStates ? StatesWithDisabled(props.unavailableStates) : States()}
                      />
                    </div>
                  </Col>
                </Row>

                <Row className='mt-2'>
                  <Col>
                    <div className='form-group mb-3'>
                      <TextInput
                        name="height"
                        id="height"
                        label="Height"
                        value={formState.height}
                        onChange={(e) => handleValueChange('height', e.target.value)}
                        InputProps={{
                          inputComponent: InputMaskHeight,
                        }}
                      />
                    </div>
                  </Col>

                  <Col>
                    <div className='form-group mb-3'>
                      <TextInput
                        name="weight"
                        id="weight"
                        label="Weight"
                        value={formState.weight}
                        onChange={(e) => handleValueChange('weight', e.target.value)}
                        InputProps={{
                          inputComponent: InputMaskWeight,
                        }}
                      />
                    </div>
                  </Col>
                </Row>

                {!props.isAmerico ? <Row className='mt-2'>
                  <Col>
                    <FormGroup>
                      <StyledRadioInput
                        name="tobacco"
                        id="tobacco"
                        label="Have you used any form of tobacco or nicotine in the last 12 months?"
                        value={formState.tobacco}
                        options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                        onChange={e => handleValueChange('tobacco', e)}
                        isBlankIcon={true}
                        isBlankCheckMark={false}
                      />
                    </FormGroup>
                  </Col>
                </Row> :
                  <Row className='mt-2'>
                    <Col>
                      <div className='form-group mb-3'>
                        <SelectInput
                          name="tobacco"
                          id="tobacco"
                          value={formState.tobacco}
                          onChange={(e) => handleValueChange('tobacco', e.target.value)}
                          label="Tobacco/Nicotine Use"
                          selectOptions={props.tobaccoOptions || TobaccoUseExtended()}
                          aria-invalid="true"
                          aria-describedby="state"
                        />
                      </div>
                    </Col>
                  </Row>}
              </Col>
              {purlMismatch &&
                <Col xs={12}>
                  <ModalWarning message='Your quotes have been refreshed to reflect the information you provided. Take a moment to review the updated results and choose the product that best aligns with your needs.' />
                </Col>
              }

              {props.showQuoteButton &&
                <Col xs={12}>
                  <Button variant="contained" color="primary" type="submit" fullWidth className='submit-button'>
                    <ShieldCheckmarkIcon style={{ width: "20px" }} className='me-1' />{props.isPurlVerification ? (purlMismatch ? "BACK TO RESULTS" : "CONTINUE") : "UPDATE MY QUOTE"}</Button>
                </Col>
              }
            </Row>

            {props.quickQuoteDisclaimer && props.submitButtonVariant === 'landing' &&
              <Row>
                <Col className="quick-quote-disclaimer-container"
                  dangerouslySetInnerHTML={{ __html: props.quickQuoteDisclaimer }} />
              </Row>
            }
            {props.term30EnableTeaserRate &&
              <Row>
                <Col className='d-flex flex-column align-items-center coverage-detail'>
                  {loading && !props.openEditPopUp ?
                    <Col lg={4} xs={12} sm={12} md={12}>
                      <div className="loader-container">
                        <CircularProgress />
                      </div>
                    </Col>
                    : teaserRate && !props.hideCoverage && (Object.keys(errors).length === 0) &&
                    <React.Fragment>
                      <div className='coverage-amount'>Pay as little as</div>
                      <div className="quote-amount-dollars-container">
                        <NumericFormat
                          className={`quote-amount-dollars-inner primary-color`}
                          value={teaserRate || 'No quotes available'}
                          displayType={'text'}
                          thousandSeparator={true}
                          decimalScale={2}
                          fixedDecimalScale={true}
                        />
                        <span className={`quote-amount-mo`}>/mo</span>
                      </div>
                      <div>for ${coverageAmountAsPerAge} &mdash; {termLengthAsPerAge} year coverage</div>
                    </React.Fragment>
                  }
                </Col>
              </Row>
            }
            <Row className="mt-0">
              {!props.submitButtonVariant && !props.hideNextButton &&
                <Col className="text-end pb-3">
                  <Button variant="contained" size="medium" color="primary" type="submit">
                    NEXT
                  </Button>
                </Col>
              }
              {props.submitButtonVariant && props.submitButtonVariant === 'landing' &&
                <Col className="d-flex mt-3">
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    className='submit-button'
                    disabled={loading && isPartnerReferralEmailCaptureActive}>
                    {(loading && isPartnerReferralEmailCaptureActive && !props.term30EnableTeaserRate) ? <CircularProgress style={{ marginRight: 15 }} color="inherit" size={20} />
                      : <ShieldCheckmarkIcon className="button-icon" />} SEE YOUR QUOTES
                  </Button>
                </Col>
              }
            </Row>
            {props.showTestimonialPanel && <TestimonialPanel />}
          </Form>
        </FormProvider>
      </div>
    </ThemeProvider>
  );
});

TermAboutMeForm.ValidationSchema = (ageSpecs) => {
  return object().shape({
    gender: string().required('Gender is required.'),
    birthdate: date()
      .typeError('Invalid date.')
      .max(sub(new Date(), {
        years: ageSpecs.min,
        days: ageSpecs.minAgeDays || 0,
      }), `Minimum age is ${ageSpecs.minAgeDays ? `${ageSpecs.minAgeDays} days` : ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required('Birthdate is required.'),
    state: string().required('State is required.'),
    height: customValidators.string()
      .required('Height is required.')
      .isValidEtradeHeightString('Invalid height', 36, 84),
    weight: customValidators.string()
      .required('Weight is required.')
      .isValidWeightString('Please verify your weight has been entered correctly.', 78, 395),
    tobacco: string().required('Tobacco usage is required.'),
    email: string().notRequired().email('Invalid email.').nullable(),
  });
};

TermAboutMeForm.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    gender: prefill ? 'M' : (basicInfo && basicInfo.gender) || '',
    birthdate: prefill ? '11/15/1980' : (basicInfo && basicInfo.birthdate) || '',
    tobacco: prefill ? 'No' : (basicInfo && basicInfo.tobacco) || '',
    state: prefill ? 'ID' : (basicInfo && basicInfo.state) || '',
    height: prefill ? '5 ft 10 in' : (basicInfo && basicInfo.height) || '',
    weight: prefill ? '175 pounds' : (basicInfo && basicInfo.weight) || '',
    email: prefill ? 'mailto:user@address.com' : (basicInfo && basicInfo.email) || '',
    usResident: true,
    bmi: (basicInfo && basicInfo.bmi) || undefined,
  };
};

TermAboutMeForm.propTypes = {
  brandTheme: PropTypes.string,
  ageSpecs: PropTypes.object,
  teaserQuoteParams: PropTypes.object,
  hideCoverage: PropTypes.bool,
  term30EnableTeaserRate: PropTypes.bool,
  validateBMI: PropTypes.func,
  onDeclined: PropTypes.func,
  openEditPopUp: PropTypes.bool,
  setOpenEditPopUp: PropTypes.func,
  onNext: PropTypes.func,
  showHeader: PropTypes.bool,
  unavailableStates: PropTypes.array,
  showQuoteButton: PropTypes.bool,
  quickQuoteDisclaimer: PropTypes.any,
  submitButtonVariant: PropTypes.string,
  hideNextButton: PropTypes.bool,
  isPurlVerification: PropTypes.bool,
};

export default TermAboutMeForm;
